import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { WidePageLayout } from '../components/WidePageLayout';
import RichTextEditor from '../components/RichTextEditor';
import useEpics from '../hooks/useEpics';
import { useBoard } from '../hooks/useBoard';
import CheckSquareIcon from '../components/icons/CheckSquareIcon';
import type { CreateEpicRequest } from '../types/epic';

const COLOR_PRESETS = [
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#6b7280', // Gray
];

export function CreateEpic() {
  const { id: boardId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { createEpic } = useEpics(boardId || '0');
  const { board } = useBoard(parseInt(boardId || '0', 10));

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLOR_PRESETS[5]); // Default to blue
  const [content, setContent] = useState<any[]>([{ children: [{ text: '' }] }]);
  const [parentEpicId, setParentEpicId] = useState<number | undefined>(undefined);
  const [completedListId, setCompletedListId] = useState<number | undefined>(undefined);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    const epicData: CreateEpicRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      content:
        content.length > 1 || (content[0] as any).children[0].text !== ''
          ? (content as unknown as Record<string, unknown>)
          : undefined,
      color,
      parent_epic_id: parentEpicId,
      completed_list_id: completedListId,
    };

    try {
      const newEpic = await createEpic(epicData);
      navigate(`/boards/${boardId}/epics/${newEpic.id}`);
    } catch {
      // Error is handled by hook
    }
  };

  return (
    <div className="space-y-6">
      <WidePageLayout>
        <div>
          <Link
            to={`/boards/${boardId}/epics`}
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            ← Back to Epics
          </Link>
          <h1 className="text-3xl font-bold text-white mt-2">Create Epic</h1>
          <p className="text-gray-400 mt-1">Create a new epic for this board</p>
        </div>
      </WidePageLayout>

      <WidePageLayout>
        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          {/* Name */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-white font-medium mb-2">
              Epic Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              placeholder="Enter epic name..."
              required
            />
          </div>

          {/* Description */}
          <div className="mb-6">
            <label htmlFor="description" className="block text-white font-medium mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 resize-none"
              rows={3}
              placeholder="Brief description of this epic..."
            />
          </div>

          {/* Color */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">Color</label>
            <div className="flex flex-wrap gap-3">
              {COLOR_PRESETS.map((presetColor) => (
                <button
                  key={presetColor}
                  type="button"
                  onClick={() => setColor(presetColor)}
                  className={`w-10 h-10 rounded-lg border-2 transition-all ${
                    color === presetColor
                      ? 'border-white ring-2 ring-white ring-opacity-50'
                      : 'border-transparent hover:border-gray-500'
                  }`}
                  style={{ backgroundColor: presetColor }}
                  title={presetColor}
                />
              ))}
            </div>
          </div>

          {/* Rich Text Content */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">Epic Details</label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Add detailed information about this epic..."
              className="border-0"
            />
            <p className="text-gray-500 text-xs mt-2">
              Use the rich text editor to add formatted content, lists, and more.
            </p>
          </div>

          {/* Parent Epic */}
          <div className="mb-6">
            <label htmlFor="parentEpic" className="block text-white font-medium mb-2">
              Parent Epic
            </label>
            <select
              id="parentEpic"
              value={parentEpicId || ''}
              onChange={(e) =>
                setParentEpicId(e.target.value ? parseInt(e.target.value, 10) : undefined)
              }
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">No parent (top-level epic)</option>
              {/* Parent epics would be loaded here */}
            </select>
            <p className="text-gray-500 text-xs mt-2">
              Select a parent epic to create a hierarchy. Leave empty for a top-level epic.
            </p>
          </div>

          {/* Completed List */}
          <div className="mb-6">
            <label htmlFor="completedList" className="block text-white font-medium mb-2">
              Completed List
            </label>
            <select
              id="completedList"
              value={completedListId || ''}
              onChange={(e) =>
                setCompletedListId(e.target.value ? parseInt(e.target.value, 10) : undefined)
              }
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">No completed list</option>
              {board?.lists?.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
            <p className="text-gray-500 text-xs mt-2">
              Select the list that represents completed cards for this epic. Used to calculate
              progress.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <Link
              to={`/boards/${boardId}/epics`}
              className="px-6 py-2 rounded-lg border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={!name.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <span className="w-5 h-5">
                <CheckSquareIcon />
              </span>
              Create Epic
            </button>
          </div>
        </form>
      </WidePageLayout>
    </div>
  );
}

export default CreateEpic;
